import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { 
  ArrowLeft, User, BookOpen, ShieldAlert, Loader2, 
  GraduationCap, DoorClosed, Calendar, Shirt, AlertCircle, Plus,
  History, FileText, Pencil, Trash2
} from "lucide-react";
import api from "../../connector/api";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import { UniformInfractionModal } from "../components/StudentDetail/UniformInfractionModal";
import DisciplinaryReferralModal from "../components/StudentDetail/DisciplinaryReferralModal";

const DEFAULT_AVATAR = "https://encrypted-tbn1.gstatic.com/licensed-image?q=tbn:ANd9GcQTiJkgZ0mkuqUowo79OFdygZXX4sgLAPdovRrejmAptDHJ1FHTKIahKR6PudWjkufkUFVpXUXwfVj8fgI";

const TeacherStudentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imgSrc, setImgSrc] = useState(null);

  // Active Tab State ('overview' | 'behavior' | 'uniform')
  const [activeTab, setActiveTab] = useState("overview");

  // Layout States
  const [permissions, setPermissions] = useState([]);
  const [user, setUser] = useState({ first_name: "Teacher", last_name: "User", email: "teacher@school.edu" });
  const [assignedClassesList, setAssignedClassesList] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [loadingPeriod, setLoadingPeriod] = useState(true);
  const notificationDropdownRef = useRef(null);

  // Modal Controls & Submission Status
  const [isUniformModalOpen, setIsUniformModalOpen] = useState(false);
  const [isBehaviorModalOpen, setIsBehaviorModalOpen] = useState(false);
  const [submittingDiscipline, setSubmittingDiscipline] = useState(false);

  // Behavior Edit & Delete State
  const [selectedLog, setSelectedLog] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingDiscipline, setDeletingDiscipline] = useState(false);

  // Uniform Edit & Delete State
  const [selectedUniformLog, setSelectedUniformLog] = useState(null);
  const [isDeleteUniformModalOpen, setIsDeleteUniformModalOpen] = useState(false);
  const [deletingUniform, setDeletingUniform] = useState(false);

  useEffect(() => {
    const storedPerms = sessionStorage.getItem("permissions");
    const storedUser = sessionStorage.getItem("user");
    if (storedPerms) { try { setPermissions(JSON.parse(storedPerms)); } catch (e) {} }
    if (storedUser) { try { setUser(JSON.parse(storedUser)); } catch (e) {} }

    const fetchInitialData = async () => {
      try {
        setLoadingPeriod(true);
        const [periodRes, subjectsRes] = await Promise.all([
          api.get("year-terms/get-set-year-term/"),
          api.get("teacher/subjects/")
        ]);
        setCurrentPeriod(periodRes.data);
        setAssignedClassesList(subjectsRes.data.results || subjectsRes.data || []);
      } catch (err) {
        console.error("Failed to load metadata:", err);
      } finally {
        setLoadingPeriod(false);
      }
    };
    fetchInitialData();
  }, []);

  const fetchStudentDetail = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);

      const [detailRes, behaviorRes, uniformRes] = await Promise.allSettled([
        api.get(`teacher-students-detail/${id}/`),
        api.get(`teacher/discipline-referrals/?student=${id}`),
        api.get(`teacher/uniform-infractions/?student=${id}`)
      ]);

      if (detailRes.status === "fulfilled") {
        const data = detailRes.value.data;
        
        const behaviorLogs = behaviorRes.status === "fulfilled"
          ? (behaviorRes.value.data.results || behaviorRes.value.data || [])
          : (data.behavior_logs || []);

        const uniformLogs = uniformRes.status === "fulfilled"
          ? (uniformRes.value.data.results || uniformRes.value.data || [])
          : (data.uniform_infractions_log || []);

        setStudent({
          ...data,
          behavior_logs: behaviorLogs,
          uniform_infractions_log: uniformLogs,
          total_uniform_infractions: uniformLogs.length
        });

        setImgSrc(data.profile_picture || DEFAULT_AVATAR);
      } else {
        throw detailRes.reason;
      }
    } catch (err) {
      console.error("Failed to load student record:", err);
      setError("Unable to retrieve student profile.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudentDetail();
  }, [id]);

  // --- Behavior Handlers ---
  const handleOpenEditModal = (log) => {
    setSelectedLog(log);
    setIsBehaviorModalOpen(true);
  };

  const handleOpenCreateModal = () => {
    setSelectedLog(null);
    setIsBehaviorModalOpen(true);
  };

  const handleOpenDeleteModal = (log) => {
    setSelectedLog(log);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedLog) return;
    try {
      setDeletingDiscipline(true);
      await api.delete(`teacher/discipline-referrals/${selectedLog.id}/`);
      toast.success("Disciplinary referral deleted successfully!");
      setIsDeleteModalOpen(false);
      setSelectedLog(null);
      await fetchStudentDetail();
    } catch (err) {
      console.error("Failed to delete disciplinary referral:", err);
      toast.error("Failed to delete referral. Please try again.");
    } finally {
      setDeletingDiscipline(false);
    }
  };

  const handleSubmitDiscipline = async (payload) => {
    try {
      setSubmittingDiscipline(true);

      const targetStudentId = student?.id || student?.student_id || id;
      const targetClassroomId = 
        student?.classroom_id || 
        student?.classroom_details?.id || 
        student?.classroom?.id;

      const submissionData = {
        student: targetStudentId,
        classroom: targetClassroomId,
        school_year_term: currentPeriod?.id,
        ...payload,
      };

      if (selectedLog?.id) {
        await api.patch(`teacher/discipline-referrals/${selectedLog.id}/`, submissionData);
        toast.success("Disciplinary referral updated successfully!");
      } else {
        await api.post("teacher/discipline-referrals/", submissionData);
        toast.success("Disciplinary referral logged successfully!");
      }

      await fetchStudentDetail();
      setIsBehaviorModalOpen(false);
      setSelectedLog(null);
    } catch (err) {
      console.error("Failed to submit disciplinary referral:", err);
      toast.error("Failed to save referral. Please try again.");
      throw err;
    } finally {
      setSubmittingDiscipline(false);
    }
  };

  // --- Uniform Handlers ---
  const handleOpenCreateUniformModal = () => {
    setSelectedUniformLog(null);
    setIsUniformModalOpen(true);
  };

  const handleOpenEditUniformModal = (item) => {
    setSelectedUniformLog(item);
    setIsUniformModalOpen(true);
  };

  const handleOpenDeleteUniformModal = (item) => {
    setSelectedUniformLog(item);
    setIsDeleteUniformModalOpen(true);
  };

  const handleConfirmDeleteUniform = async () => {
    if (!selectedUniformLog) return;
    try {
      setDeletingUniform(true);
      await api.delete(`teacher/uniform-infractions/${selectedUniformLog.id}/`);
      toast.success("Uniform infraction deleted successfully!");
      setIsDeleteUniformModalOpen(false);
      setSelectedUniformLog(null);
      await fetchStudentDetail();
    } catch (err) {
      console.error("Failed to delete uniform infraction:", err);
      toast.error("Failed to delete infraction. Please try again.");
    } finally {
      setDeletingUniform(false);
    }
  };

  const handleUniformSuccess = async () => {
    await fetchStudentDetail();
    toast.success(
      selectedUniformLog ? "Uniform infraction updated successfully!" : "Uniform infraction logged successfully!"
    );
    setSelectedUniformLog(null);
  };

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = "/login";
  };

  const yearDisplay = currentPeriod?.school_year?.name || currentPeriod?.school_year_detail?.name || "";
  const termDisplay = currentPeriod?.term?.name || currentPeriod?.term_detail?.name || "";

  const canEditBehavior = permissions.includes("teacher_can_edit_behavior") || permissions.includes("edit_discipline");
  const canDeleteBehavior = permissions.includes("teacher_can_delete_behavior") || permissions.includes("delete_discipline");
  const canEditUniform = permissions.includes("teacher_can_edit_dresscode") || permissions.includes("edit_uniform");
  const canDeleteUniform = permissions.includes("teacher_can_delete_dresscode") || permissions.includes("delete_uniform");

  return (
    <div className="flex h-screen w-full bg-zinc-100 text-zinc-800 font-dynamic-title antialiased overflow-hidden select-none">
      <Toaster position="top-right" reverseOrder={false} />

      <Sidebar
        permissions={permissions}
        user={user}
        teacherSubjects={assignedClassesList}
        handleLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Header
          loading={loadingPeriod}
          loadingPeriod={loadingPeriod}
          yearDisplay={yearDisplay}
          termDisplay={termDisplay}
          notifOpen={notifOpen}
          setNotifOpen={setNotifOpen}
          notificationDropdownRef={notificationDropdownRef}
        />

        <main className="flex-1 overflow-y-auto p-6 custom-scroll font-dynamic-title">
          <div className="max-w-7xl mx-auto space-y-6">
            
            {/* Top Bar */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-zinc-200 rounded-lg text-xs font-semibold text-zinc-700 hover:bg-zinc-50 transition-colors shadow-sm cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4 text-zinc-500" />
                Back to Student List
              </button>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={!student?.id && !student?.student_id}
                  onClick={handleOpenCreateModal}
                  className="disabled:opacity-50 inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 border border-rose-200 rounded-lg text-xs font-semibold text-rose-700 hover:bg-rose-100 transition-colors shadow-sm cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Log Incident
                </button>
                <button
                  type="button"
                  disabled={!student?.id && !student?.student_id}
                  onClick={handleOpenCreateUniformModal}
                  className="disabled:opacity-50 inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors shadow-sm cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Log Infraction
                </button>
              </div>
            </div>

            {loading ? (
              <div className="bg-white border border-zinc-200 rounded-xl p-12 flex items-center justify-center gap-2 text-zinc-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-xs font-medium">Loading student details...</span>
              </div>
            ) : error || !student ? (
              <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-xs text-rose-500">
                {error || "Student record not found."}
              </div>
            ) : (
              <div className="space-y-6">
                
                {/* Profile Header */}
                <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row items-center sm:items-start gap-6">
                  <img
                    src={imgSrc}
                    onError={() => setImgSrc(DEFAULT_AVATAR)}
                    alt={`${student.last_name}, ${student.first_name}`}
                    className="w-28 h-28 rounded-xl object-cover border border-zinc-200 bg-zinc-50 shadow-inner"
                  />
                  <div className="flex-1 text-center sm:text-left space-y-2">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <h1 className="text-sm font-bold text-zinc-900">
                          {student.last_name}, {student.first_name}
                        </h1>
                        <p className="text-xs font-mono text-zinc-500">
                          ID: {student.student_id_number || "N/A"}
                        </p>
                      </div>
                      <div className="flex items-center justify-center sm:justify-start gap-2">
                        <span className="px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-700 font-semibold text-xs capitalize">
                          {student.gender || "Unspecified"}
                        </span>
                        {student.has_uniform_infraction ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold text-xs">
                            <ShieldAlert className="w-3.5 h-3.5" />
                            Infraction Flagged Today
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold text-xs">
                            Clean Uniform Status
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-center sm:justify-start gap-1.5 pt-1">
                      <GraduationCap className="w-4 h-4 text-zinc-400" />
                      <span className="px-2.5 py-1 rounded bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-100">
                        Classroom: {student.classroom_name || "Unassigned"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Tab Navigation */}
                <div className="flex items-center gap-2 border-b border-zinc-200 pb-1">
                  <button
                    onClick={() => setActiveTab("overview")}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-t-lg transition-colors cursor-pointer ${
                      activeTab === "overview"
                        ? "bg-white border-t border-x border-zinc-200 text-blue-600 -mb-px"
                        : "text-zinc-500 hover:text-zinc-700"
                    }`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Overview
                  </button>
                  <button
                    onClick={() => setActiveTab("behavior")}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-t-lg transition-colors cursor-pointer ${
                      activeTab === "behavior"
                        ? "bg-white border-t border-x border-zinc-200 text-blue-600 -mb-px"
                        : "text-zinc-500 hover:text-zinc-700"
                    }`}
                  >
                    <AlertCircle className="w-3.5 h-3.5" />
                    Behavior Logs ({student.behavior_logs?.length || 0})
                  </button>
                  <button
                    onClick={() => setActiveTab("uniform")}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-t-lg transition-colors cursor-pointer ${
                      activeTab === "uniform"
                        ? "bg-white border-t border-x border-zinc-200 text-blue-600 -mb-px"
                        : "text-zinc-500 hover:text-zinc-700"
                    }`}
                  >
                    <Shirt className="w-3.5 h-3.5" />
                    Uniform Infractions ({student.uniform_infractions_log?.length || 0})
                  </button>
                </div>

                {/* Tab Content */}
                {activeTab === "overview" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm space-y-4">
                      <h2 className="text-xs font-bold text-zinc-800 uppercase tracking-wider border-b border-zinc-100 pb-2">
                        Classroom & Advisory Information
                      </h2>
                      {student.classroom_details ? (
                        <div className="space-y-3 text-xs">
                          <div className="flex items-center gap-3 text-zinc-600">
                            <BookOpen className="w-4 h-4 text-zinc-400 shrink-0" />
                            <div>
                              <p className="text-[10px] text-zinc-400">Section Name</p>
                              <p className="font-medium">{student.classroom_details.section_name || "N/A"}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-zinc-600">
                            <DoorClosed className="w-4 h-4 text-zinc-400 shrink-0" />
                            <div>
                              <p className="text-[10px] text-zinc-400">Room Code</p>
                              <p className="font-medium">{student.classroom_details.room_code || "N/A"}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-zinc-600">
                            <User className="w-4 h-4 text-zinc-400 shrink-0" />
                            <div>
                              <p className="text-[10px] text-zinc-400">Homeroom Adviser</p>
                              <p className="font-medium">{student.adviser_name || "N/A"}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-zinc-600">
                            <Calendar className="w-4 h-4 text-zinc-400 shrink-0" />
                            <div>
                              <p className="text-[10px] text-zinc-400">School Year</p>
                              <p className="font-medium">{student.classroom_details.school_year || "N/A"}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-zinc-400 italic">No classroom details assigned.</p>
                      )}
                    </div>

                    <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm space-y-4">
                      <h2 className="text-xs font-bold text-zinc-800 uppercase tracking-wider border-b border-zinc-100 pb-2">
                        Conduct Summary
                      </h2>
                      <div className="space-y-3 text-xs">
                        <div className="flex items-center justify-between p-2.5 rounded-lg bg-zinc-50 border border-zinc-100">
                          <span className="text-zinc-600 font-medium">Current Conduct Rating</span>
                          <span className={`font-semibold px-2 py-0.5 rounded text-[11px] ${
                            student.behavior_rating === "Needs Improvement" 
                              ? "bg-rose-100 text-rose-700"
                              : student.behavior_rating === "Satisfactory"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-emerald-100 text-emerald-700"
                          }`}>
                            {student.behavior_rating || "Good Standing"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-2.5 rounded-lg bg-zinc-50 border border-zinc-100">
                          <span className="text-zinc-600 font-medium">Uniform Violations (Term)</span>
                          <span className="font-bold text-zinc-800">
                            {student.total_uniform_infractions ?? 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "behavior" && (
                  <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm space-y-4">
                    <h2 className="text-xs font-bold text-zinc-800 uppercase tracking-wider border-b border-zinc-100 pb-2 flex items-center gap-2">
                      <History className="w-4 h-4 text-zinc-500" />
                      Behavior Incident History
                    </h2>

                    {student.behavior_logs && student.behavior_logs.length > 0 ? (
                      <div className="space-y-3">
                        {student.behavior_logs.map((log, index) => {
                          const levelKey = log.infraction_level?.toLowerCase() || log.severity?.toLowerCase() || "";
                          const severityColors = {
                            minor: "bg-blue-50 text-blue-700 border-blue-200",
                            moderate: "bg-amber-50 text-amber-700 border-amber-200",
                            major: "bg-rose-50 text-rose-700 border-rose-200",
                            severe: "bg-rose-100 text-rose-800 border-rose-300",
                          };

                          const statusColors = {
                            resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
                            pending: "bg-amber-50 text-amber-700 border-amber-200",
                            under_review: "bg-purple-50 text-purple-700 border-purple-200",
                          };

                          const severityClass = severityColors[levelKey] || "bg-zinc-100 text-zinc-700 border-zinc-200";
                          const statusClass = statusColors[log.status?.toLowerCase()] || "bg-zinc-100 text-zinc-700 border-zinc-200";

                          const incidentTitle = log.violation_type_name || log.violation_type || log.category || "Behavioral Incident";
                          const incidentDate = log.date_of_incident || log.created_at ? new Date(log.date_of_incident || log.created_at).toLocaleDateString() : log.date || "N/A";
                          const incidentDescription = log.incident_description || log.description || log.notes || "No details provided.";
                          const actionTaken = log.vp_notes || log.action_taken || log.resolution;
                          const teacherName = 
                            typeof log.referring_teacher === "object" && log.referring_teacher !== null
                              ? log.referring_teacher.full_name || `${log.referring_teacher.first_name} ${log.referring_teacher.last_name}`
                              : log.referring_teacher_name || "Staff Member";

                          return (
                            <div key={log.id || index} className="p-4 bg-zinc-50 border border-zinc-200 rounded-lg text-xs space-y-3 shadow-sm">
                              
                              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-200/60 pb-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-zinc-800 text-sm">
                                    {incidentTitle}
                                  </span>
                                  
                                  {(log.infraction_level || log.severity) && (
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border capitalize ${severityClass}`}>
                                      {log.infraction_level || log.severity}
                                    </span>
                                  )}
                                </div>

                                <div className="flex items-center gap-2">
                                  {log.status && (
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border capitalize ${statusClass}`}>
                                      {log.status.replace("_", " ")}
                                    </span>
                                  )}
                                  <span className="text-[10px] text-zinc-500 font-mono">
                                    {incidentDate} {log.time_of_incident ? `@ ${log.time_of_incident}` : ""}
                                  </span>

                                  <div className="flex items-center gap-1 ml-2 border-l border-zinc-200 pl-2">
                                    {canEditBehavior && (
                                      <button
                                        type="button"
                                        onClick={() => handleOpenEditModal(log)}
                                        className="p-1 rounded text-zinc-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                        title="Edit Incident"
                                      >
                                        <Pencil className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    {canDeleteBehavior && (
                                      <button
                                        type="button"
                                        onClick={() => handleOpenDeleteModal(log)}
                                        className="p-1 rounded text-zinc-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                        title="Delete Incident"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div>
                                <p className="text-zinc-700 leading-relaxed">
                                  {incidentDescription}
                                </p>
                              </div>

                              {log.interventions && log.interventions.length > 0 && (
                                <div className="p-2 bg-white rounded border border-zinc-200/80 space-y-1">
                                  <span className="font-semibold text-[10px] text-zinc-500 uppercase tracking-wide">
                                    Classroom Interventions Attempted ({log.interventions.length}):
                                  </span>
                                  <ul className="list-disc list-inside space-y-0.5 text-zinc-600 text-[11px]">
                                    {log.interventions.map((item, idx) => (
                                      <li key={item.id || idx}>
                                        <strong>{item.intervention_type}</strong> ({item.date_attempted})
                                        {item.other_details && ` - ${item.other_details}`}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {actionTaken && (
                                <div className="p-2.5 bg-zinc-100/80 rounded border border-zinc-200/60 text-zinc-700 space-y-0.5">
                                  <span className="font-semibold text-[10px] text-zinc-500 uppercase tracking-wide">
                                    VP Notes / Action Taken:
                                  </span>
                                  <p className="text-xs text-zinc-800">{actionTaken}</p>
                                </div>
                              )}

                              <div className="flex flex-wrap items-center justify-between gap-y-1 text-[11px] text-zinc-500 pt-1 border-t border-zinc-200/40">
                                <span>
                                  <strong>Referring Teacher:</strong> {teacherName}
                                </span>
                                {log.location && (
                                  <span>
                                    <strong>Location:</strong> {log.location}
                                  </span>
                                )}
                              </div>

                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-zinc-400 italic">No behavioral incidents logged.</p>
                    )}
                  </div>
                )}

                {activeTab === "uniform" && (
                  <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm space-y-4">
                    <div className="border-b border-zinc-100 pb-2 flex items-center justify-between">
                      <h2 className="text-xs font-bold text-zinc-800 uppercase tracking-wider flex items-center gap-2">
                        <History className="w-4 h-4 text-zinc-500" />
                        Uniform Infraction History
                      </h2>
                    </div>

                    {student.uniform_infractions_log && student.uniform_infractions_log.length > 0 ? (
                      <div className="space-y-3">
                        {student.uniform_infractions_log.map((item) => {
                          const itemsList = typeof item.specific_item === "string"
                            ? item.specific_item.split(",").map((s) => s.trim()).filter(Boolean)
                            : Array.isArray(item.specific_item)
                            ? item.specific_item
                            : [];

                          return (
                            <div
                              key={item.id}
                              className="p-3 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs space-y-2 hover:border-zinc-300 transition-colors"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex flex-wrap gap-1 items-center">
                                  {itemsList.length > 0 ? (
                                    itemsList.map((tag, idx) => (
                                      <span
                                        key={idx}
                                        className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded font-medium text-[11px]"
                                      >
                                        {tag}
                                      </span>
                                    ))
                                  ) : (
                                    <span className="font-semibold text-amber-700">
                                      Uniform Violation
                                    </span>
                                  )}
                                </div>

                                <div className="flex items-center gap-2 shrink-0">
                                  <span className="text-[10px] text-zinc-400">
                                    {item.created_at
                                      ? new Date(item.created_at).toLocaleDateString(undefined, {
                                          month: "short",
                                          day: "numeric",
                                          year: "numeric",
                                        })
                                      : item.date || "N/A"}
                                  </span>

                                  <div className="flex items-center gap-1 border-l border-zinc-200 pl-2">
                                    {canEditUniform && (
                                      <button
                                        type="button"
                                        onClick={() => handleOpenEditUniformModal(item)}
                                        className="p-1 text-zinc-400 hover:text-zinc-700 rounded transition-colors cursor-pointer"
                                        title="Edit Infraction"
                                      >
                                        <Pencil className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    {canDeleteUniform && (
                                      <button
                                        type="button"
                                        onClick={() => handleOpenDeleteUniformModal(item)}
                                        className="p-1 text-zinc-400 hover:text-rose-600 rounded transition-colors cursor-pointer"
                                        title="Delete Infraction"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-zinc-500 text-[11px]">
                                {item.intervention_applied && (
                                  <span className="capitalize">
                                    <strong className="text-zinc-600">Intervention:</strong>{" "}
                                    {item.intervention_applied}
                                  </span>
                                )}
                                {item.subject_name && (
                                  <span>
                                    <strong className="text-zinc-600">Class:</strong>{" "}
                                    {item.subject_name}
                                  </span>
                                )}
                              </div>

                              {item.detailed_note && (
                                <p className="text-zinc-600 pt-1 border-t border-zinc-200/40 text-xs leading-relaxed">
                                  {item.detailed_note}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-zinc-400 italic">No uniform infractions logged.</p>
                    )}
                  </div>
                )}

              </div>
            )}
          </div>
        </main>
      </div>

      {/* --- MODALS --- */}
      <UniformInfractionModal
        isOpen={isUniformModalOpen}
        onClose={() => {
          setIsUniformModalOpen(false);
          setSelectedUniformLog(null);
        }}
        studentId={student?.id || student?.student_id || id}
        currentPeriodId={currentPeriod?.id}
        assignedClassesList={assignedClassesList}
        existingInfraction={selectedUniformLog}
        onSuccess={handleUniformSuccess}
      />

      <DisciplinaryReferralModal
        isOpen={isBehaviorModalOpen}
        onClose={() => {
          setIsBehaviorModalOpen(false);
          setSelectedLog(null);
        }}
        studentId={student?.id || student?.student_id || id}
        classroomId={
          student?.classroom_id || 
          student?.classroom_details?.id || 
          student?.classroom?.id
        }
        currentPeriodId={currentPeriod?.id}
        assignedClassesList={assignedClassesList}
        initialData={selectedLog}
        onSubmit={handleSubmitDiscipline}
        onSuccess={fetchStudentDetail}
        submitting={submittingDiscipline}
      />

      {/* --- BEHAVIOR DELETE MODAL --- */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-white rounded-xl shadow-lg border border-zinc-200 max-w-sm w-full p-6 space-y-4">
            <div className="space-y-1">
              <h3 className="text-sm font-bold text-zinc-900">Confirm Deletion</h3>
              <p className="text-xs text-zinc-600">
                Are you sure you want to delete this disciplinary record? This action cannot be undone.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setSelectedLog(null);
                }}
                className="px-3 py-1.5 text-xs font-semibold text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
                disabled={deletingDiscipline}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deletingDiscipline}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors shadow-sm disabled:opacity-50 cursor-pointer"
              >
                {deletingDiscipline && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Delete Record
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- UNIFORM DELETE MODAL --- */}
      {isDeleteUniformModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-white rounded-xl shadow-lg border border-zinc-200 max-w-sm w-full p-6 space-y-4">
            <div className="space-y-1">
              <h3 className="text-sm font-bold text-zinc-900">Confirm Deletion</h3>
              <p className="text-xs text-zinc-600">
                Are you sure you want to delete this uniform infraction log? This action cannot be undone.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsDeleteUniformModalOpen(false);
                  setSelectedUniformLog(null);
                }}
                className="px-3 py-1.5 text-xs font-semibold text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
                disabled={deletingUniform}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteUniform}
                disabled={deletingUniform}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors shadow-sm disabled:opacity-50 cursor-pointer"
              >
                {deletingUniform && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Delete Log
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherStudentDetail;